import React, { useState } from 'react';
import { Code, ArrowLeft, CheckCircle2, Info, Rocket, Download } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import Button from '../../components/ui/Button';
import Spinner from '../../components/ui/Spinner';
import { IntegrationCodeViewer } from '../../components/design-phase';
import { api } from '../../lib/api';
import { downloadPackage } from '../../lib/api/designPhaseApi';
import type { IntegrationPackage } from '../../types/designPhase';

interface Step8Props {
  caseId: string;
  caseData: any;
  onNext: () => void;
  onBack: () => void;
}

export const Step8CodeGeneration: React.FC<Step8Props> = ({
  caseId,
  caseData,
  onNext,
  onBack,
}) => {
  const [generatedPackage, setGeneratedPackage] = useState<IntegrationPackage | null>(null);
  const [isComplete, setIsComplete] = useState(false);

  const handlePackageGenerated = (pkg: IntegrationPackage) => {
    setGeneratedPackage(pkg);
    setIsComplete(true);
    console.log('Package generated:', pkg);
  };

  // Fetch the ZIP through the configured API client, then hand it to the
  // browser as an object URL. The package is read from Neo4j, so this works on
  // a later visit or after a backend restart.
  const downloadMutation = useMutation({
    mutationFn: async (packageId: string) => {
      const blob = await downloadPackage(packageId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `integration-package-${caseId}-${packageId}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
    onSuccess: () => {
      toast.success('Package downloaded');
    },
    onError: (error: any) => {
      console.error('Failed to download package:', error);
      toast.error('Could not download the package', {
        description:
          error?.response?.status === 404
            ? 'The package was not found. Try regenerating it.'
            : error?.message || 'Please try again.',
      });
    },
  });

  // Complete Design phase mutation.
  //
  // Earlier this PATCHed `currentPhase: 'Deployment'`, but the backend
  // CasePhase enum only accepts Discovery | Analysis | Design — so every
  // call 422'd silently (no toast, only console.error), leaving the case
  // with designPhaseStatus never advancing.  Users thought they'd
  // completed the phase; the backend never knew.
  //
  // Fix: keep currentPhase at 'Design' (Design is the terminal phase in
  // the current model), but advance `designPhaseStatus` to 'Completed'
  // so the dashboard badge + CaseDetail flags reflect reality.  Add a
  // toast on success/failure so the user actually sees what happened.
  const completeDesignMutation = useMutation({
    mutationFn: async () => {
      // Generate and persist the three design-phase artifacts before marking the
      // phase complete.
      //
      // These endpoints each write a node the design-summary reads —
      // (:Case)-[:HAS_ROADMAP|HAS_INGESTION_DESIGN|HAS_DEPLOYMENT_CONFIG]->.
      // Nothing called them, so all three were always absent and design
      // completeness was pinned at 0% no matter how much of the phase was done.
      //
      // Each is independent and non-fatal: the code package is already
      // generated by this point, so one artifact failing must not block
      // completion. Failures are collected and surfaced, not swallowed.
      const artifacts: Array<[string, Promise<unknown>]> = [
        ['value roadmap', api.post(`/case-analysis/cases/${caseId}/value-roadmap`, {})],
        ['ingestion design', api.get(`/case-analysis/cases/${caseId}/ingestion-design`)],
        ['deployment config', api.post(`/case-analysis/cases/${caseId}/deployment-config`, {})],
      ];

      const results = await Promise.allSettled(artifacts.map(([, p]) => p));
      const failed = results
        .map((r, i) => (r.status === 'rejected' ? artifacts[i][0] : null))
        .filter((name): name is string => name !== null);

      if (failed.length) {
        console.warn('Design artifacts not persisted:', failed);
      }

      await api.patch(`/cases/${caseId}`, {
        designPhaseStatus: 'Completed',
      });
      return { caseId, failed };
    },
    onSuccess: ({ failed }) => {
      if (failed.length) {
        toast.success('Design phase completed', {
          description: `Could not save: ${failed.join(', ')}. The code package is unaffected.`,
        });
      } else {
        toast.success('Design phase completed', {
          description: 'Your case is now ready for deployment.',
        });
      }
      onNext();
    },
    onError: (error: any) => {
      console.error('Failed to complete Design phase:', error);
      toast.error('Failed to complete Design phase', {
        description: error?.message || 'Please try again or contact support.',
      });
    },
  });

  const handleComplete = () => {
    completeDesignMutation.mutate();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Code className="w-6 h-6 text-purple-600" />
          <h2 className="text-2xl font-bold text-neutral-900">
            Step 8: Code Generation
          </h2>
        </div>
        <p className="text-neutral-600">
          Generate deployment-ready integration code and artifacts
        </p>
      </div>

      {/* Info Banner */}
      <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
        <div className="flex gap-3">
          <Info className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-purple-900">
            <p className="font-medium mb-1">Generated Artifacts</p>
            <p className="mb-2">
              Based on your data flow mappings and integration patterns, the following code
              artifacts will be generated:
            </p>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                <span>ETL Scripts (Python/PySpark)</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                <span>Cypher Queries (MERGE/MATCH)</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                <span>Airflow DAGs</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                <span>Docker Configuration</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                <span>Schema DDL Scripts</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                <span>Documentation (README)</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Code Viewer Component */}
      <div className="border rounded-lg bg-white">
        <IntegrationCodeViewer
          caseId={caseId}
          onPackageGenerated={handlePackageGenerated}
        />
      </div>

      {/* Completion Status */}
      {isComplete && generatedPackage && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-green-900">
              <p className="font-medium mb-1">Integration Package Ready</p>
              <p>
                Your integration code has been generated successfully. You can download
                the complete package or proceed to deployment preparation.
              </p>
              <div className="mt-3 flex gap-2">
                {/* Goes through the API client rather than a raw href: a bare
                    absolute path bypasses VITE_API_BASE_URL (hitting the Vite
                    dev server locally) and sends no auth header. */}
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => downloadMutation.mutate(generatedPackage.package_id)}
                  disabled={downloadMutation.isPending}
                >
                  <Download className="w-4 h-4 mr-1.5" />
                  {downloadMutation.isPending ? 'Preparing…' : 'Download Package'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Integration Pattern
        </Button>
        <Button
          onClick={handleComplete}
          disabled={completeDesignMutation.isPending}
        >
          {completeDesignMutation.isPending ? (
            <>
              <Spinner size="sm" className="mr-2" />
              Completing...
            </>
          ) : (
            <>
              <Rocket className="w-4 h-4 mr-2" />
              Complete Design Phase
            </>
          )}
        </Button>
      </div>

      {/* Error Display */}
      {completeDesignMutation.isError && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-900 font-medium">
            Failed to complete Design phase. Please try again.
          </p>
        </div>
      )}
    </div>
  );
};

export default Step8CodeGeneration;
